// =============================================================================
// Chat Store - État global du module chat (Zustand)
// =============================================================================

import { create } from 'zustand';
import type { ChatChannel, ChatMessage } from '../types';
import {
  fetchChannels,
  fetchDMChannels,
  createChannel,
  updateChannel,
  deleteChannel,
  togglePinChannel,
  markChannelRead,
  findOrCreateDM,
  resolveCurrentMemberId,
  fetchUnreadCounts,
  fetchChannelScopes,
  softDeleteConversation,
  creatorDeleteGroup,
  getMemberDeletedAt,
} from '../data/channelRepository';
import {
  fetchMessages,
  fetchThreadMessages,
  fetchMessageById,
  sendMessage,
  editMessage,
  deleteMessage,
} from '../data/messageRepository';
import { toggleReaction } from '../data/reactionRepository';
import { linkTaskToMessage } from '../data/taskLinkRepository';
import { fetchOrganizationMembers } from '@/modules/core/data/memberRepository';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// Ref-counted realtime subscription to avoid duplicates (sidebar + ChatPage)
let _realtimeRefCount = 0;
let _realtimeUnsub: (() => void) | null = null;
let _realtimeOrgId: string | null = null;
let _unreadPollInterval: ReturnType<typeof setInterval> | null = null;
let _lastUnreadSnapshot: Record<string, number> | null = null;
// Raw, un-defensively-zeroed snapshot from the most recent server fetch.
// Used by markRead so the "Nouveaux messages" separator still appears
// when the active-channel zero-out in loadUnreadCounts wins the race.
let _lastServerUnreadCounts: Record<string, number> = {};
const _lastToastAtByChannel = new Map<string, number>();
// Tracks the last time the local store optimistically bumped a channel's
// unread count. Used to prevent the polling/RPC reconciliation from racing
// against a just-received realtime INSERT and visually clearing the badge
// before the server-side count catches up.
const _lastOptimisticBumpAt = new Map<string, number>();
const OPTIMISTIC_BUMP_PROTECT_MS = 4000;

type ChatMessageChangeRow = Pick<ChatMessage, 'id' | 'channel_id' | 'sender_member_id' | 'content' | 'parent_message_id'>;
type ChatChannelChangeRow = Pick<ChatChannel, 'id' | 'is_dm' | 'created_by'>;
type ChatChannelMemberChangeRow = { channel_id?: string; org_member_id?: string };

function getMessagePreview(content?: string | null): string {
  const text = (content || '').replace(/\s+/g, ' ').trim();
  if (!text) return 'Nouveau message';
  return text.length > 80 ? `${text.slice(0, 77)}…` : text;
}

function showIncomingMessageToast(channelId: string, channelName: string, content?: string | null) {
  const now = Date.now();
  const lastToastAt = _lastToastAtByChannel.get(channelId) || 0;
  if (now - lastToastAt < 2500) return;
  _lastToastAtByChannel.set(channelId, now);
  toast('💬 Nouveau message', {
    description: `${channelName} · ${getMessagePreview(content)}`,
    duration: 4000,
    closeButton: true,
  });
}

interface ChatState {
  // Data
  channels: ChatChannel[];
  activeChannelId: string | null;
  messages: ChatMessage[];
  threadMessages: ChatMessage[];
  activeThreadId: string | null;
  unreadCounts: Record<string, number>;
  openedUnreadCounts: Record<string, number>;
  activeChannelVisible: boolean;
  hasMoreMessages: boolean;
  loadingOlderMessages: boolean;

  // Loading
  loadingChannels: boolean;
  loadingMessages: boolean;
  
  // Member info (cached)
  currentMemberId: string | null;

  // Actions
  loadChannels: (orgId: string) => Promise<void>;
  loadUnreadCounts: (orgId: string) => Promise<void>;
  setActiveChannel: (channelId: string | null) => void;
  setActiveChannelVisible: (visible: boolean) => void;
  loadMessages: (channelId: string) => Promise<void>;
  loadOlderMessages: (channelId: string) => Promise<number>;
  openThread: (messageId: string) => Promise<void>;
  closeThread: () => void;
  send: (orgId: string, channelId: string, content: string, parentId?: string) => Promise<void>;
  edit: (messageId: string, content: string) => Promise<void>;
  remove: (messageId: string) => Promise<void>;
  react: (messageId: string, emoji: string) => Promise<void>;
  pin: (channelId: string, isPinned: boolean) => Promise<void>;
  markRead: (channelId: string) => Promise<void>;
  createDM: (orgId: string, otherMemberId: string) => Promise<ChatChannel>;
  createNewChannel: (params: { orgId: string; scopeType: 'workspace' | 'space' | 'project'; scopeId?: string; name: string; createdBy?: string; icon?: string }) => Promise<ChatChannel>;
  removeChannel: (channelId: string) => Promise<void>;
  archiveChannel: (channelId: string) => Promise<void>;
  softDelete: (orgId: string, channelId: string) => Promise<void>;
  creatorDelete: (orgId: string, channelId: string, creatorName: string) => Promise<void>;
  linkTask: (orgId: string, messageId: string, taskId: string, linkType: 'created_from' | 'linked') => Promise<void>;
  
  // Realtime
  subscribeRealtime: (orgId: string) => () => void;
  
  // Init
  initMemberId: (orgId: string) => Promise<void>;
}

export const useChatStore = create<ChatState>((set, get) => ({
  channels: [],
  activeChannelId: null,
  messages: [],
  threadMessages: [],
  activeThreadId: null,
  unreadCounts: {},
  openedUnreadCounts: {},
  activeChannelVisible: false,
  hasMoreMessages: false,
  loadingOlderMessages: false,
  loadingChannels: false,
  loadingMessages: false,
  currentMemberId: null,

  initMemberId: async (orgId: string) => {
    const memberId = await resolveCurrentMemberId(orgId);
    if (memberId) set({ currentMemberId: memberId });
  },

  loadChannels: async (orgId: string) => {
    set({ loadingChannels: true });
    try {
      const memberId = get().currentMemberId;
      const channels = await fetchChannels(orgId, memberId);
      set({ channels });
    } finally {
      set({ loadingChannels: false });
    }
  },

  loadUnreadCounts: async (orgId: string) => {
    const memberId = get().currentMemberId;
    if (!memberId) return;
    const counts = await fetchUnreadCounts(orgId, memberId);
    if (counts === null) return;
    // Preserve the raw server snapshot BEFORE we apply any local defensive
    // zeroing. `markRead` uses this as a fallback when computing the
    // "Nouveaux messages" separator, otherwise the active-channel zeroing
    // below would make it think there were no unread messages to mark.
    _lastServerUnreadCounts = { ...counts };
    // Defensive: the channel currently being viewed must never display as
    // unread (the user is literally looking at it). Prevents any RPC race
    // from briefly flashing a "1" badge on the active channel — including
    // for the user's own freshly-sent messages.
    const activeId = get().activeChannelId;
    if (activeId && get().activeChannelVisible) {
      counts[activeId] = 0;
    }
    // Defensive against the reverse race: when realtime just bumped a
    // channel's unread badge optimistically (+1), the next RPC tick may
    // still return the *previous* count (the server-side aggregate is
    // eventually-consistent w.r.t. realtime delivery). Honour the local
    // optimistic value for a short protection window so the badge never
    // visibly drops back to its old value before the user reacts.
    const now = Date.now();
    const local = get().unreadCounts;
    for (const [chId, ts] of _lastOptimisticBumpAt.entries()) {
      if (now - ts > OPTIMISTIC_BUMP_PROTECT_MS) {
        _lastOptimisticBumpAt.delete(chId);
        continue;
      }
      if (chId === activeId && get().activeChannelVisible) continue;
      const localCount = local[chId] || 0;
      if (localCount > (counts[chId] || 0)) {
        counts[chId] = localCount;
      }
    }
    set({ unreadCounts: counts });
  },

  setActiveChannel: (channelId) => {
    set({
      activeChannelId: channelId,
      messages: [],
      activeThreadId: null,
      threadMessages: [],
      hasMoreMessages: false,
      loadingOlderMessages: false,
    });
    try {
      if (channelId) {
        localStorage.setItem('chat:lastActiveChannelId', channelId);
      } else {
        localStorage.removeItem('chat:lastActiveChannelId');
      }
    } catch {
      // ignore storage errors (private mode, etc.)
    }
  },

  setActiveChannelVisible: (visible) => {
    set({ activeChannelVisible: visible });
  },

  loadMessages: async (channelId: string) => {
    set({ loadingMessages: true });
    try {
      // Check if user has a soft-delete date for this channel
      const channel = get().channels.find(c => c.id === channelId);
      const afterDate = channel?._deleted_at || undefined;
      const PAGE_SIZE = 100;
      const messages = await fetchMessages(channelId, { parentOnly: true, afterDate, limit: PAGE_SIZE });
      set({
        messages,
        // Optimistic guess: if we filled the page, assume there might be older
        // ones. The next page request will confirm/disprove.
        hasMoreMessages: messages.length >= PAGE_SIZE,
      });
    } finally {
      set({ loadingMessages: false });
    }
  },

  loadOlderMessages: async (channelId: string) => {
    const state = get();
    if (state.loadingOlderMessages || !state.hasMoreMessages) return 0;
    if (state.activeChannelId !== channelId) return 0;
    if (state.messages.length === 0) return 0;
    const oldest = state.messages[0];
    const channel = state.channels.find(c => c.id === channelId);
    const afterDate = channel?._deleted_at || undefined;
    set({ loadingOlderMessages: true });
    try {
      const PAGE_SIZE = 100;
      const older = await fetchMessages(channelId, {
        parentOnly: true,
        afterDate,
        limit: PAGE_SIZE,
        before: oldest.created_at,
      });
      if (older.length === 0) {
        set({ hasMoreMessages: false });
        return 0;
      }
      set(s => {
        // Guard against channel switch during the await.
        if (s.activeChannelId !== channelId) return s;
        // Merge, dedup by id, keep chronological order.
        const seen = new Set(s.messages.map(m => m.id));
        const merged = [...older.filter(m => !seen.has(m.id)), ...s.messages];
        return {
          messages: merged,
          hasMoreMessages: older.length >= PAGE_SIZE,
        };
      });
      return older.length;
    } finally {
      set({ loadingOlderMessages: false });
    }
  },

  openThread: async (messageId: string) => {
    set({ activeThreadId: messageId, threadMessages: [] });
    const threadMessages = await fetchThreadMessages(messageId);
    set({ threadMessages });
  },

  closeThread: () => {
    set({ activeThreadId: null, threadMessages: [] });
  },

  send: async (orgId, channelId, content, parentId) => {
    const memberId = get().currentMemberId;
    if (!memberId) return;
    const msg = await sendMessage({
      orgId,
      channelId,
      senderMemberId: memberId,
      content,
      parentMessageId: parentId,
    });
    
    const now = msg.created_at;
    if (parentId) {
      set(s => ({ threadMessages: [...s.threadMessages, msg] }));
    } else {
      set(s => ({
        messages: [...s.messages, msg],
        // Bump the channel to the top via last_message timestamp
        channels: s.channels.map(c =>
          c.id === channelId
            ? { ...c, last_message: { created_at: now } as any, updated_at: now }
            : c
        ),
      }));
    }

    // Mark the channel as read for the sender so their own message
    // never counts as unread (RPC excludes own messages, but we also
    // bump last_read_at to keep the local snapshot in sync and avoid
    // any stale increment from the polling fallback).
    if (!parentId) {
      try {
        await markChannelRead(channelId, memberId);
        set(s => ({
          unreadCounts: { ...s.unreadCounts, [channelId]: 0 },
        }));
      } catch (err) {
        console.warn('[Chat] Failed to auto-mark channel read after send', err);
      }
    }

    // Detect @mentions and create notifications (fire-and-forget)
    if (content.includes('@')) {
      import('../data/chatNotificationRepository').then(async ({ resolveMentionedMemberIds, createMentionNotifications }) => {
        try {
          // Load org members via repository
          const membersData = await fetchOrganizationMembers(orgId, 'active');
          if (membersData.length === 0) {
            console.warn('[Chat Mentions] No members found for org', orgId);
            return;
          }

          const members = membersData.map((m) => ({
            member_id: m.id,
            full_name: m.profile?.full_name || null,
            email: m.profile?.email || '',
          }));

          console.log('[Chat Mentions] Resolving mentions in:', content, 'against', members.length, 'members');
          const mentionedIds = resolveMentionedMemberIds(content, members);
          console.log('[Chat Mentions] Resolved IDs:', mentionedIds);
          if (mentionedIds.length === 0) return;

          const channel = get().channels.find(c => c.id === channelId);
          const senderProfile = membersData.find((m) => m.id === memberId);

          await createMentionNotifications({
            orgId,
            channelId,
            messageId: msg.id,
            senderMemberId: memberId,
            senderName: senderProfile?.profile?.full_name || 'Quelqu\'un',
            channelName: channel?.name || 'Discussion',
            messageContent: content,
            mentionedMemberIds: mentionedIds,
          });
          console.log('[Chat Mentions] Notifications created successfully');
        } catch (err) {
          console.error('[Chat Mentions] Error creating notifications:', err);
        }
      });
    }
  },

  edit: async (messageId, content) => {
    await editMessage(messageId, content);
    const updateMsg = (msgs: ChatMessage[]) =>
      msgs.map(m => m.id === messageId ? { ...m, content, is_edited: true } : m);
    set(s => ({
      messages: updateMsg(s.messages),
      threadMessages: updateMsg(s.threadMessages),
    }));
  },

  remove: async (messageId) => {
    await deleteMessage(messageId);
    set(s => ({
      messages: s.messages.filter(m => m.id !== messageId),
      threadMessages: s.threadMessages.filter(m => m.id !== messageId),
    }));
  },

  react: async (messageId, emoji) => {
    const memberId = get().currentMemberId;
    if (!memberId) return;
    await toggleReaction(messageId, memberId, emoji);
    // Reload message reactions
    const channelId = get().activeChannelId;
    if (channelId) {
      get().loadMessages(channelId);
    }
  },

  pin: async (channelId, isPinned) => {
    const memberId = get().currentMemberId;
    if (!memberId) return;
    await togglePinChannel(channelId, memberId, isPinned);
    set(s => ({
      channels: s.channels.map(c =>
        c.id === channelId ? { ...c, _pinned: isPinned } : c
      ),
    }));
  },

  markRead: async (channelId) => {
    const memberId = get().currentMemberId;
    if (!memberId) return;
    // Use the highest of: local (possibly zeroed for the active channel),
    // raw server snapshot (before defensive zeroing), so we still know how
    // many messages were unread when the user opened the channel.
    const localUnread = get().unreadCounts[channelId] || 0;
    const serverUnread = _lastServerUnreadCounts[channelId] || 0;
    const previousUnread = Math.max(localUnread, serverUnread);
    await markChannelRead(channelId, memberId);
    // Reset unread count locally and keep a short-lived visual marker for
    // the channel that has just been opened, so users can see where the
    // unread messages started even after the server receipt is persisted.
    set(s => ({
      unreadCounts: { ...s.unreadCounts, [channelId]: 0 },
      openedUnreadCounts: previousUnread > 0
        ? { ...s.openedUnreadCounts, [channelId]: previousUnread }
        : { ...s.openedUnreadCounts, [channelId]: 0 },
    }));
  },

  createDM: async (orgId, otherMemberId) => {
    const memberId = get().currentMemberId;
    if (!memberId) throw new Error('Not initialized');
    const channel = await findOrCreateDM(orgId, memberId, otherMemberId);
    set(s => ({
      channels: s.channels.some(c => c.id === channel.id) ? s.channels : [channel, ...s.channels],
    }));
    await get().loadChannels(orgId);
    return channel;
  },

  createNewChannel: async (params) => {
    const channel = await createChannel({
      orgId: params.orgId,
      scopeType: params.scopeType,
      scopeId: params.scopeId,
      name: params.name,
      createdBy: params.createdBy,
      icon: params.icon,
    });
    await get().loadChannels(params.orgId);
    return channel;
  },

  removeChannel: async (channelId) => {
    await deleteChannel(channelId);
    set(s => ({
      channels: s.channels.filter(c => c.id !== channelId),
      activeChannelId: s.activeChannelId === channelId ? null : s.activeChannelId,
      messages: s.activeChannelId === channelId ? [] : s.messages,
    }));
  },

  archiveChannel: async (channelId) => {
    await updateChannel(channelId, { is_archived: true });
    set(s => ({
      channels: s.channels.filter(c => c.id !== channelId),
      activeChannelId: s.activeChannelId === channelId ? null : s.activeChannelId,
    }));
  },

  softDelete: async (orgId, channelId) => {
    const memberId = get().currentMemberId;
    if (!memberId) return;
    await softDeleteConversation(channelId, memberId);
    set(s => ({
      channels: s.channels.filter(c => c.id !== channelId),
      activeChannelId: s.activeChannelId === channelId ? null : s.activeChannelId,
      messages: s.activeChannelId === channelId ? [] : s.messages,
    }));
  },

  creatorDelete: async (orgId, channelId, creatorName) => {
    await creatorDeleteGroup(channelId, creatorName);
    // Reload channels to reflect the change
    await get().loadChannels(orgId);
    set(s => ({
      activeChannelId: s.activeChannelId === channelId ? null : s.activeChannelId,
      messages: s.activeChannelId === channelId ? [] : s.messages,
    }));
  },

  linkTask: async (orgId, messageId, taskId, linkType) => {
    const memberId = get().currentMemberId;
    if (!memberId) return;
    await linkTaskToMessage({ orgId, messageId, taskId, linkType, createdBy: memberId });
    // Reload messages
    const channelId = get().activeChannelId;
    if (channelId) get().loadMessages(channelId);
  },

  subscribeRealtime: (orgId: string) => {
    // If already subscribed for this org, just increment ref count
    if (_realtimeOrgId === orgId && _realtimeUnsub) {
      _realtimeRefCount++;
      return () => {
        _realtimeRefCount--;
        if (_realtimeRefCount <= 0) {
          _realtimeUnsub?.();
          _realtimeUnsub = null;
          _realtimeOrgId = null;
          _realtimeRefCount = 0;
        }
      };
    }

    // Clean up any previous subscription for a different org
    if (_realtimeUnsub) {
      _realtimeUnsub();
      _realtimeUnsub = null;
      _realtimeOrgId = null;
      _realtimeRefCount = 0;
    }

    const channel = supabase
      .channel(`org:${orgId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'chat_messages',
        filter: `org_id=eq.${orgId}`,
      }, async (payload) => {
        const newMsg = payload.new as ChatMessageChangeRow;
        const state = get();
        const msgTime = (payload.new as any).created_at as string || new Date().toISOString();
        
        const isViewingChannel = newMsg.channel_id === state.activeChannelId && state.activeChannelVisible;

        // Always bump the channel's last_message so the sidebar re-sorts
        if (!newMsg.parent_message_id) {
          set(s => ({
            channels: s.channels.map(c =>
              c.id === newMsg.channel_id
                ? { ...c, last_message: { created_at: msgTime } as any, updated_at: msgTime }
                : c
            ),
          }));
        }

        // If this is the active, visible channel: append the single new message
        // instead of reloading the whole list (avoids loader flash / "refresh" effect).
        if (isViewingChannel) {
          if (!newMsg.parent_message_id && !state.messages.find(m => m.id === newMsg.id)) {
            try {
              const full = await fetchMessageById(newMsg.id);
              if (full) {
                set(s => (
                  s.messages.find(m => m.id === full.id)
                    ? s
                    : { messages: [...s.messages, full] }
                ));
              }
            } catch (err) {
              console.warn('[Chat] Failed to fetch incoming message, falling back to reload', err);
              state.loadMessages(newMsg.channel_id);
            }
          }
          if (newMsg.sender_member_id !== state.currentMemberId) {
            await state.markRead(newMsg.channel_id);
            _lastUnreadSnapshot = get().unreadCounts;
          } else {
            // Own message in the active channel: force-clear any badge
            // that may have been set by an earlier RPC tick, so the user
            // never sees a "+1" on their own send.
            set(s => ({
              unreadCounts: { ...s.unreadCounts, [newMsg.channel_id]: 0 },
            }));
          }
        } else if (!newMsg.parent_message_id) {
          // Only recipient-visible messages count as unread; never count our own sends.
          if (newMsg.sender_member_id !== state.currentMemberId) {
            const previousCount = state.unreadCounts[newMsg.channel_id] || 0;
            if (!state.channels.find(c => c.id === newMsg.channel_id)) {
              await state.loadChannels(orgId);
            }
            // Optimistic local increment so the badge bumps immediately for
            // every incoming message, even if the RPC refresh is debounced
            // or the polling tick hasn't fired yet.
            set(s => ({
              unreadCounts: {
                ...s.unreadCounts,
                [newMsg.channel_id]: (s.unreadCounts[newMsg.channel_id] || 0) + 1,
              },
            }));
            // Mark this channel as recently bumped so the next RPC tick
            // doesn't visually erase the badge before the server catches up.
            _lastOptimisticBumpAt.set(newMsg.channel_id, Date.now());
            // Reconcile with server truth in the background.
            state.loadUnreadCounts(orgId).then(() => {
              _lastUnreadSnapshot = get().unreadCounts;
            }).catch(() => undefined);

            const channelName = get().channels.find(c => c.id === newMsg.channel_id)?.name || 'Discussion';
            const nextCount = get().unreadCounts[newMsg.channel_id] || 0;
            if (nextCount > previousCount) {
              showIncomingMessageToast(newMsg.channel_id, channelName, newMsg.content);
            }
          }
        }
        
        // Thread messages
        if (newMsg.parent_message_id && newMsg.parent_message_id === state.activeThreadId) {
          if (!state.threadMessages.find(m => m.id === newMsg.id)) {
            try {
              const full = await fetchMessageById(newMsg.id);
              if (full) {
                set(s => (
                  s.threadMessages.find(m => m.id === full.id)
                    ? s
                    : { threadMessages: [...s.threadMessages, full] }
                ));
              }
            } catch {
              state.openThread(newMsg.parent_message_id);
            }
          }
        }
      })
      .on('postgres_changes', {
        event: 'DELETE',
        schema: 'public',
        table: 'chat_messages',
        filter: `org_id=eq.${orgId}`,
      }, (payload) => {
        const deleted = payload.old as Pick<ChatMessage, 'id'>;
        set(s => ({
          messages: s.messages.filter(m => m.id !== deleted.id),
          threadMessages: s.threadMessages.filter(m => m.id !== deleted.id),
        }));
      })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'chat_reactions',
      }, () => {
        const state = get();
        if (state.activeChannelId) {
          state.loadMessages(state.activeChannelId);
        }
      })
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'chat_channels',
        filter: `org_id=eq.${orgId}`,
      }, async (payload) => {
        const newChannel = payload.new as ChatChannelChangeRow;
        const state = get();
        // A 1:1 DM is created before the first message is sent. Do not reveal it
        // to the recipient from the channel event alone; the message event will
        // load it once it has actual content. Group conversations are still
        // handled through chat_channel_members below.
        if (newChannel.is_dm && newChannel.created_by !== state.currentMemberId) {
          return;
        }
        // Only add if not already in the list
        if (!state.channels.find(c => c.id === newChannel.id)) {
          // Reload channels to get full data with scopes
          await state.loadChannels(orgId);
        }
      })
      .on('postgres_changes', {
        event: 'DELETE',
        schema: 'public',
        table: 'chat_channels',
        filter: `org_id=eq.${orgId}`,
      }, (payload) => {
        const deleted = payload.old as Pick<ChatChannel, 'id'>;
        const state = get();
        // Remove channel from the list
        set(s => ({
          channels: s.channels.filter(c => c.id !== deleted.id),
        }));
        // If the deleted channel was active, clear it
        if (state.activeChannelId === deleted.id) {
          set({ activeChannelId: null, messages: [] });
        }
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'chat_channels',
        filter: `org_id=eq.${orgId}`,
      }, async () => {
        // Reload channels on updates (archive, rename, etc.)
        const state = get();
        await state.loadChannels(orgId);
      })
      .subscribe();

    _realtimeOrgId = orgId;
    _realtimeRefCount = 1;
    // Initialize snapshot from a fresh server fetch BEFORE starting the
    // polling loop. Without this, the very first poll tick compares against
    // a possibly-empty in-memory state (right after a page refresh) and
    // mis-fires the "Nouveau message" toast for every already-existing
    // unread channel. Snapshot stays null until the first authoritative
    // server snapshot is loaded; the poll skips toasts while null.
    _lastUnreadSnapshot = null;
    get().loadUnreadCounts(orgId).then(() => {
      _lastUnreadSnapshot = { ...get().unreadCounts };
    }).catch(() => {
      _lastUnreadSnapshot = { ...get().unreadCounts };
    });
    _unreadPollInterval = setInterval(async () => {
      const state = get();
      if (!state.currentMemberId) return;
      // Skip toast emission until we have a baseline server snapshot.
      const baselineReady = _lastUnreadSnapshot !== null;
      const previousCounts = _lastUnreadSnapshot || {};
      await state.loadUnreadCounts(orgId);
      const nextCounts = get().unreadCounts;
      const activeVisibleChannelId = get().activeChannelVisible ? get().activeChannelId : null;
      const increasedChannelId = Object.entries(nextCounts).find(([channelId, count]) => (
        channelId !== activeVisibleChannelId && count > (previousCounts[channelId] || 0)
      ))?.[0];

      if (baselineReady && increasedChannelId) {
        if (!get().channels.find(c => c.id === increasedChannelId)) {
          await get().loadChannels(orgId);
        }
        const channelName = get().channels.find(c => c.id === increasedChannelId)?.name || 'Discussion';
        // Fetch the latest message of the channel so the toast shows the same
        // preview as the realtime path (otherwise the polling fallback only
        // shows "Nouveau message" without context).
        let latestContent: string | null = null;
        try {
          const { data: latest } = await supabase
            .from('chat_messages')
            .select('content')
            .eq('channel_id', increasedChannelId)
            .is('parent_message_id', null)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          latestContent = latest?.content ?? null;
        } catch { /* keep null */ }
        showIncomingMessageToast(increasedChannelId, channelName, latestContent);
      }
      _lastUnreadSnapshot = nextCounts;
    }, 15000);
    // Batch 3 (B3.4): unread polling reduced from 3s to 15s. Realtime on
    // chat_messages remains the primary toast/badge path; polling stays as a
    // fallback to recover from missed realtime events.
    // Separate channel for membership changes — keeps the main message
    // subscription robust (multiplexing many bindings + unfiltered table can
    // tip the realtime channel into CHANNEL_ERROR in CI).
    const memberId = get().currentMemberId;
    const memberChannel = memberId
      ? supabase
          .channel(`presence:${orgId}`)
          .on('postgres_changes', {
            event: '*',
            schema: 'public',
            table: 'chat_channel_members',
            filter: `org_member_id=eq.${memberId}`,
          }, async () => {
            const state = get();
            await state.loadChannels(orgId);
            await state.loadUnreadCounts(orgId);
          })
          .subscribe()
      : null;
    _realtimeUnsub = () => {
      if (_unreadPollInterval) {
        clearInterval(_unreadPollInterval);
        _unreadPollInterval = null;
      }
      _lastUnreadSnapshot = null;
      supabase.removeChannel(channel);
      if (memberChannel) supabase.removeChannel(memberChannel);
    };

    return () => {
      _realtimeRefCount--;
      if (_realtimeRefCount <= 0) {
        _realtimeUnsub?.();
        _realtimeUnsub = null;
        _realtimeOrgId = null;
        _realtimeRefCount = 0;
      }
    };
  },
}));
